import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { isEmpty } from 'lodash';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
// import { FiInfo } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import PromptService from '~services/prompt';
import NotificationsActions from '~redux/notifications';
import { renderFirstName } from '~helpers/common';

import Stars from '~components/stars';
import Form from '~components/forms/form';
import Button from '~components/button';
import Combobox from '~components/forms/combobox';
import { TextareaControlled } from '~components/forms/textarea';
import CharacterCount from '~components/forms/character-count';

import { updateReview, createReview } from '~services/reviews';

const MAX_COMMENT_LENGTH = 200;

const ReviewForm = ({
  authorId,
  receiverId,
  score,
  comment = '',
  reviewId,
  receiverFirstName = '',
  action = 'create',
  // isReviewModalOpened,
  // setIsReviewModalOpened
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fName = renderFirstName(receiverFirstName);

  const validationSchema = Yup.object().shape({
    score: Yup.number().required().min(1).max(5),
    comment: Yup.string().max(MAX_COMMENT_LENGTH).noEmailInContent().noPhoneInContent().noUrlInContent(),
  });

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      score,
      comment,
    },
  });

  const watchComment = methods.watch('comment');

  useEffect(async () => {
    if (!isSubmitting) {
      return;
    }

    const formData = methods.getValues();

    try {
      const token = await getAccessTokenSilently();
      if (action === 'create') {
        await createReview({ authorId, receiverId, score: formData.score, text: formData.comment, token });
      }
      if (action === 'edit') {
        await updateReview({ reviewId, authorId, receiverId, score: formData.score, text: formData.comment, token });
      }

      dispatch(
        NotificationsActions.addNotification(
          intl.formatMessage({ id: 'containers.review.notification', defaultMessage: 'Review saved successfully.' })
        )
      );
      setIsSubmitted(true);
    } catch (err) {
      console.log('Could not submit review', err);
      PromptService.error(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting]);

  const handleFormSubmit = () => {
    if (isEmpty(methods.errors) || !isSubmitting) {
      PromptService.confirm({
        title: intl.formatMessage({ id: 'containers.review.prompt.title', defaultMessage: 'Are you sure?' }),
        content: intl.formatMessage({
          id: 'containers.review.prompt.content',
          defaultMessage: 'Are you sure that you want to submit this review',
        }),
        ok: intl.formatMessage({
          id: 'containers.review.prompt.ok',
          defaultMessage: "Yes, I'm sure",
        }),
      }).then((response) => {
        if (typeof response === 'undefined') {
          return;
        }
        setIsSubmitting(true);
      });
    }
  };

  return (
    <Form methods={methods} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
      {isSubmitted ? (
        <div className="c-review__thank-you">
          {intl.formatMessage({
            id: 'containers.review.thank-you',
            defaultMessage: 'Thank you for submitting your review.',
          })}
          <div className="l-mt2">
            <Link to="/account">
              <Button className="c-btn--secondary">
                {intl.formatMessage({
                  id: 'containers.review.goto-profile',
                  defaultMessage: 'Go to profile.',
                })}
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="c-review-page__description">
            <p>
              {intl.formatMessage(
                {
                  id: 'containers.review.content',
                  defaultMessage:
                    'Please rate your recent experience with {firstName}. Select the number of stars and save.',
                },
                { firstName: fName }
              )}
            </p>
            {/* <FiInfo
              className="c-widget__info"
              onClick={() => {
                setIsReviewModalOpened(true);
              }}
            /> */}
          </div>
          <div className="c-fieldset">
            <Combobox.Controlled
              name="score"
              placeholder={(
                <div className="c-review-page__option">
                  <div className="c-review-page__rate">
                    {intl.formatMessage(
                      {
                        id: 'containers.review.stars',
                        defaultMessage: '{value, plural, =1 {# star} other { # stars}}',
                      },
                      { value: 5 }
                    )}
                  </div>
                  <Stars value={5} disabled />
                </div>
              )}
            >
              {Array(5)
                .fill()
                .map((s, index) => (
                  <Combobox.Option value={index + 1} key={index + 1}>
                    <div className="c-review-page__option">
                      <div className="c-review-page__rate">
                        {intl.formatMessage(
                          {
                            id: 'containers.review.stars',
                            defaultMessage: '{value, plural, =1 {# star} other { # stars}}',
                          },
                          { value: index + 1 }
                        )}
                      </div>
                      <Stars value={index + 1} />
                    </div>
                  </Combobox.Option>
                ))}
            </Combobox.Controlled>
          </div>
          <div className="c-fieldset">
            <div className="c-fieldset__header">
              <div className="c-fieldset__label">
                {intl.formatMessage({
                  id: 'containers.review.comment.label',
                  defaultMessage: 'Comment',
                })}
              </div>
              <CharacterCount field={watchComment} max={MAX_COMMENT_LENGTH} className="c-fieldset__info" />
            </div>
            <div className="c-fieldset__value">
              <TextareaControlled
                name="comment"
                className="c-review-page__note-textarea"
                placeholder={intl.formatMessage({
                  id: 'containers.review.comment.placeholder',
                  defaultMessage: 'Write something about your cooperation',
                })}
                isAutoExpand
              />
            </div>
          </div>
          <div className="c-fieldset">
            <Button
              name="submit"
              type="submit"
              className="c-btn"
              disabled={!isEmpty(methods.errors) || isSubmitting}
              isLoading={isSubmitting}
            >
              {intl.formatMessage({
                id: 'containers.review.save',
                defaultMessage: 'Save',
              })}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default ReviewForm;
